import React, { useState, useEffect } from 'react';
import { Users, ArrowLeft, User, Phone, Save, AlertCircle, Eye, EyeOff, Crown, Shield } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, usersAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFlashMessage } from '../components/FlashMessage';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/phoneFormatter';

const EditUserForm = () => {
  const { user, isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const { id, userId } = useParams();
  const editUserId = id || userId; // Support both :id and :userId params
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Check if user is owner (has owner role in any location) but NOT system admin
  const isOwner = !isSystemAdmin() && user?.user_type === 'patron';

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    phone: '',
    full_name: '',
    user_type: 'patron',
    is_active: true
  });

  useEffect(() => {
    if (isSystemAdmin() || isOwner) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUserId, isSystemAdmin, isOwner]);

  const loadUserData = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Owner: Get all users and find by ID (no single user endpoint)
      // Admin: Get single user directly
      let userData;

      if (isOwner) {
        const response = await usersAPI.getUsers();
        if (response.data.success) {
          userData = response.data.data.find(u => u.id === parseInt(editUserId));
        }
      } else {
        // Admin: Call /api/admin/users/:id directly
        const response = await adminAPI.getUser(editUserId);
        if (response.data.success) {
          userData = response.data.data;
        }
      }

      if (userData) {
        // Admin can only edit patron users, not staff or system_admin
        if (isSystemAdmin() && userData.user_type !== 'patron') {
          setError('Sistem yöneticileri sadece patron kullanıcılarını düzenleyebilir. Staff ve sistem admin kullanıcıları düzenlenemez.');
          setIsLoading(false);
          return;
        }

        setFormData({
          username: userData.username || '',
          password: '', // Password field is empty for editing
          phone: formatPhoneNumber(userData.phone || ''),
          full_name: userData.full_name || '',
          user_type: userData.user_type || 'patron',
          is_active: userData.is_active === 1 || userData.is_active === true
        });
      } else {
        setError('Kullanıcı bulunamadı');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'username') {
      // Only allow English letters and numbers for username
      const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: filteredValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    // Basic validation - password is optional for editing
    if (formData.password && formData.password.length < 6) {
      showDanger('Şifre girilecekse en az 6 karakter olmalıdır');
      setIsSaving(false);
      return;
    }

    if (!formData.phone.trim()) {
      showDanger('Cep telefonu numarası zorunludur');
      setIsSaving(false);
      return;
    }

    try {
      let updateData;

      if (isOwner) {
        // Owner can only update: phone, full_name, password
        updateData = {
          phone: cleanPhoneNumber(formData.phone) || undefined,
          full_name: formData.full_name,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
      } else {
        // Admin can update all fields
        updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        // Clean phone number by removing formatting
        if (updateData.phone) {
          updateData.phone = cleanPhoneNumber(updateData.phone);
        }
      }

      // Use usersAPI for owner, adminAPI for admin
      const updateAPI = isOwner ? usersAPI : adminAPI;
      await updateAPI.updateUser(editUserId, updateData);

      const userTypeText = formData.user_type === 'patron' ? 'Patron' : formData.user_type === 'staff' ? 'Personel' : 'Sistem Yöneticisi';
      showSuccess(`${userTypeText} başarıyla güncellendi!`);
      setTimeout(() => {
        navigate(isOwner ? '/owner/users' : '/admin/users');
      }, 2000);
    } catch (err) {
      console.error('Failed to update user:', err);
      showDanger(`Kullanıcı güncellenirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isSystemAdmin() && !isOwner) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={isOwner ? "/owner/users" : "/admin/users"}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-7 h-7 mr-3 text-blue-500" />
              Kullanıcı Düzenle
            </h1>
            <p className="text-gray-600">Kullanıcı bilgilerini güncelleyin</p>
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Kullanıcı Bilgileri
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Kullanıcı Adı *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: patron1"
                  required
                  minLength={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  En az 3 karakter, sadece İngilizce harf ve rakam (a-z, A-Z, 0-9)
                </p>
              </div>
              <div>
                <label className="form-label">
                  Tam Adı *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: Ahmet Yılmaz"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Cep Telefonu *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0555 123 45 67"
                  maxLength="14"
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  <User className="w-4 h-4 inline mr-2" />
                  Kullanıcı Türü
                </label>
                <div className="form-input bg-gray-50 flex items-center">
                  {formData.user_type === 'patron' && <Crown className="w-4 h-4 mr-2 text-yellow-500" />}
                  {formData.user_type === 'staff' && <User className="w-4 h-4 mr-2 text-green-500" />}
                  {formData.user_type === 'system_admin' && <Shield className="w-4 h-4 mr-2 text-red-500" />}
                  <span className="text-gray-700">
                    {formData.user_type === 'patron' ? 'Patron (Lokasyon Sahibi)' :
                     formData.user_type === 'staff' ? 'Personel' :
                     'Sistem Yöneticisi'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Kullanıcı türü düzenlenemez
                </p>
              </div>
            </div>

            <div>
              <label className="form-label">
                Şifre (Boş bırakılırsa değiştirilmez)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-input pr-20"
                  placeholder="Yeni şifre (isteğe bağlı)"
                  minLength={6}
                />
                <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  >
                    Oluştur
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Güvenli şifre için "Oluştur" butonunu kullanabilirsiniz
              </p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.is_active ? 'translate-x-5' : ''}`}></div>
                </div>
                <span className="ml-3 block text-sm text-gray-900">
                  Kullanıcıyı {formData.is_active ? 'aktif' : 'pasif'} olarak kaydet
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* User Type Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              {formData.user_type === 'patron' && <Crown className="w-5 h-5 mr-2 text-yellow-500" />}
              {formData.user_type === 'staff' && <User className="w-5 h-5 mr-2 text-green-500" />}
              {formData.user_type === 'system_admin' && <Shield className="w-5 h-5 mr-2 text-red-500" />}
              {formData.user_type === 'patron' ? 'Patron' : formData.user_type === 'staff' ? 'Personel' : 'Sistem Yöneticisi'} Bilgileri
            </h3>
          </div>
          <div className="card-body">
            {formData.user_type === 'patron' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Crown className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Patron Hakları</h4>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Bu kullanıcı patron olarak güncelleniyor ve aşağıdaki yetkilere sahip olacak:</p>
                      <ul className="list-disc ml-4 mt-2 space-y-1">
                        <li>Atanan lokasyonları yönetebilme</li>
                        <li>Kendi lokasyonlarında personel ekleyebilme</li>
                        <li>Lokasyon bazlı raporları görüntüleyebilme</li>
                        <li>Müşteri ve araç yönetimi yapabilme</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {formData.user_type === 'staff' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <User className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800">Personel Hakları</h4>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Bu kullanıcı personel olarak güncelleniyor ve aşağıdaki yetkilere sahip olacak:</p>
                      <ul className="list-disc ml-4 mt-2 space-y-1">
                        <li>Atandığı lokasyonda araç check-in/out işlemleri yapabilme</li>
                        <li>Müşteri bilgilerini görüntüleyebilme</li>
                        <li>Lokasyon bazlı günlük raporları görüntüleyebilme</li>
                        <li>SMS gönderme işlemleri yapabilme</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {formData.user_type === 'system_admin' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Sistem Yöneticisi Hakları</h4>
                    <div className="mt-2 text-sm text-red-700">
                      <p>Bu kullanıcı sistem yöneticisi olarak güncelleniyor ve aşağıdaki yetkilere sahip olacak:</p>
                      <ul className="list-disc ml-4 mt-2 space-y-1">
                        <li>Tüm sistem yönetimi yetkilerine sahip olma</li>
                        <li>Lokasyon oluşturma ve yönetme</li>
                        <li>Tüm kullanıcıları yönetme (Patron, Personel)</li>
                        <li>Sistem geneli raporları görüntüleyebilme</li>
                        <li>Lokasyon sahiplerini atama ve kaldırma</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Link
            to={isOwner ? "/owner/users" : "/admin/users"}
            className="btn-secondary"
          >
            İptal
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary flex items-center"
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Güncelleniyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </form>

      {/* Flash Message Container */}
      <FlashContainer />
    </div>
  );
};

export default EditUserForm;